/**
 * SurePass color ramps, ported from the MUI 5.16 kit during the 9.0 migration.
 * Ramps not listed here (grey, amber, yellow, deepOrange, purple, green, indigo,
 * lime, lightGreen, deepPurple) are stock MUI and are intentionally not redefined.
 */

export const blueGrey = {
  50: '#f5f7f9',
  100: '#dce6ef',
  200: '#c4d2e0',
  300: '#93a8bd',
  400: '#758fa8',
  500: '#617b94',
  600: '#536d86',
  700: '#405a73',
  800: '#2b455e',
  900: '#1a344d',
  A100: '#c7d7e6',
  A200: '#aec2d5',
  A400: '#758fa8',
  A700: '#405a73',
} as const;

export const red = {
  50: '#feebee',
  100: '#fecdd4',
  200: '#ee99a5',
  300: '#e37182',
  400: '#ee4f67',
  500: '#f93b57',
  600: '#e4344e',
  700: '#d02c44',
  800: '#c3253c',
  900: '#b41930',
  A100: '#ff8698',
  A200: '#fc4f69',
  A400: '#ff193b',
  A700: '#ca001e',
} as const;

export const cyan = {
  50: '#cffffd',
  100: '#88fff9',
  200: '#3bfff5',
  300: '#00f6e9',
  400: '#00dbd0',
  500: '#00c1b7',
  600: '#00b1a8',
  700: '#009b93',
  800: '#00867f',
  900: '#00615c',
  A100: '#86fff9',
  A200: '#1cfff3',
  A400: '#00ebdf',
  A700: '#00beb4',
} as const;

export const teal = {
  50: '#c6fff2',
  100: '#71ffe0',
  200: '#02fac3',
  300: '#00ddac',
  400: '#00bb91',
  500: '#009977',
  600: '#008c6d',
  700: '#007b60',
  800: '#006b53',
  900: '#004e3d',
  A100: '#a7ffeb',
  A200: '#63ffdc',
  A400: '#02fac3',
  A700: '#00c297',
} as const;

/**
 * Figma pins `success/main` at 500 and `success/dark` at 600 (node 11033:144922).
 * The rest is the Material green ramp those two stops belong to — added whole so
 * `light` and the tints have somewhere to come from rather than being invented
 * one at a time.
 *
 * Success used to point at `teal`, which is the brand's accent and not a state:
 * a teal "Verified" chip read as decoration next to an amber warning and a red
 * error, because it was the only one of the three that was not a traffic light.
 */
export const green = {
  50: '#e8f5e9',
  100: '#c8e6c9',
  200: '#a5d6a7',
  300: '#81c784',
  400: '#66bb6a',
  500: '#4caf50',
  600: '#43a047',
  700: '#388e3c',
  800: '#2e7d32',
  900: '#1b5e20',
} as const;

export const lightBlue = {
  50: '#e8fbff',
  100: '#d1f7ff',
  200: '#b6f2ff',
  300: '#92ebff',
  400: '#6fe5ff',
  500: '#00d1ff',
  600: '#00bde6',
  700: '#009fc2',
  800: '#007791',
  900: '#00596d',
  A100: '#69e4ff',
  A200: '#27d8ff',
  A400: '#04b5dc',
  A700: '#009abc',
} as const;

export const pink = {
  50: '#fbe3f0',
  100: '#f7badc',
  200: '#f28dc5',
  300: '#ed5fad',
  400: '#e83c9b',
  500: '#e31988',
  600: '#d4177f',
  700: '#bf1573',
  800: '#ab1266',
  900: '#880e51',
  A100: '#fc7dc3',
  A200: '#fb3ca5',
  A400: '#e6007f',
  A700: '#c30f72',
} as const;

/** Only the steps SurePass overrides; the rest of each ramp stays stock MUI. */
export const orangeOverrides = { 800: '#e96900', 900: '#cf4900' } as const;
export const blueOverrides = { 700: '#1565c0', 800: '#0057b2' } as const;

/** Stock MUI amber — warning maps onto this ramp, not orange. */
export const amber = {
  400: '#ffca28',
  500: '#ffc107',
  700: '#ffa000',
  800: '#ff8f00',
  900: '#ff6f00',
} as const;

/** action/selected — a solid tint rather than an alpha, carried over from 5.16. */
export const surfaceTint = '#eff7f9';

/**
 * Named brand colors from the Brand page's "Primary and secondary colors" board.
 * These are aliases onto the ramps above, not new values.
 */
export const brand = {
  navy: blueGrey[800],
  darkTeal: teal[400],
  brightTeal: teal[200],
  skyBlue: lightBlue[500],
} as const;

/**
 * OCID credential-status gradients. Figma exposes the stops but not the geometry,
 * so the angle here is Figma's default top-to-bottom.
 */
export const ocidGradients = {
  // Dark, light, dark. A two-stop ramp put the light end at one edge, so the
  // credential sat on a wash that got steadily heavier down the screen; the
  // three-stop version lights the middle, where the card is, and falls away at
  // both edges.
  leo: `linear-gradient(180deg, ${blueGrey[900]} 0%, ${blueOverrides[700]} 50%, ${blueGrey[900]} 100%)`,
  nonLeo: `linear-gradient(180deg, ${teal[400]} 0%, ${blueGrey[900]} 100%)`,
  notVerified: `linear-gradient(180deg, ${red[800]} 0%, ${blueGrey[900]} 100%)`,
} as const;

/**
 * App chrome — the phone and tablet surfaces that do **not** follow the color
 * scheme.
 *
 * The web product flips with the mode; the app's navigation does not. Its bar
 * and toggle are the same navy in light and in dark, because the app is held at
 * arm's length in daylight and the chrome is the one fixed point a user
 * navigates by. So these are plain values, not a `light`/`dark` pair — and
 * because the fill never moves, the ink must not either. `appInk` is a literal
 * white rather than `text.primary`, which would flip to navy-on-navy in light.
 *
 * Figma reads these off the *light* primary ramp, which is why they are the
 * blueGrey steps rather than `palette.primary` — `primary.main` is lightBlue in
 * dark mode and would take the chrome with it.
 */
export const appChrome = {
  /** The App Bar, and the status strip above it. Figma blueGrey/800. */
  barFill: blueGrey[800],
  /** A step below the bar, for a row that sits under it. Figma blueGrey/900. */
  barFillDeep: blueGrey[900],
  /** The App Toggle Bar's track. Figma primary/light. */
  toggleTrack: blueGrey[700],
  /** The selected segment of a toggle bar. Figma primary/dark. */
  toggleSelected: blueGrey[900],
  /** Everything written on the chrome. */
  ink: '#ffffff',
  /** The cyan used for a checkpoint's location line. Figma lightBlue/600. */
  accent: '#00bde6',
} as const;

/**
 * Color Blooms — the brand's soft radial washes.
 *
 * Not a linear gradient: two `#00d1ff` radial glows at different opacities,
 * each fading to transparent, layered over a solid fill. That is why the app
 * header reads as navy that warms toward teal in one corner rather than as a
 * band with a direction.
 *
 * `background-image` takes a comma-separated stack painted front to back, so
 * the brighter, tighter bloom is listed first and the solid goes last.
 */
const BLOOM = '0, 209, 255';

export const colorBlooms = {
  /** The app header: one bloom low-left, a wider softer one low-right. */
  appHeader: [
    `radial-gradient(60% 55% at 18% 108%, rgba(${BLOOM}, 0.45) 0%, rgba(${BLOOM}, 0) 100%)`,
    `radial-gradient(75% 65% at 88% 96%, rgba(${BLOOM}, 0.22) 0%, rgba(${BLOOM}, 0) 100%)`,
    `linear-gradient(0deg, ${blueGrey[900]}, ${blueGrey[900]})`,
  ].join(', '),
  /** A quieter pair for a full screen behind content. */
  appScreen: [
    `radial-gradient(50% 40% at 10% 0%, rgba(${BLOOM}, 0.20) 0%, rgba(${BLOOM}, 0) 100%)`,
    `radial-gradient(60% 50% at 95% 12%, rgba(${BLOOM}, 0.12) 0%, rgba(${BLOOM}, 0) 100%)`,
    `linear-gradient(0deg, ${blueGrey[900]}, ${blueGrey[900]})`,
  ].join(', '),
} as const;

/**
 * The brand gradient, as one value.
 *
 * The Brand page has drawn `skyBlue → brightTeal` at 135° since the migration;
 * this names it so the app's OCID card and its icon orb cannot drift from the
 * swatch. It is a *brand* gradient, mode-independent like the shield it comes
 * from, so anything drawn on it takes a fixed ink.
 */
export const brandGradient = `linear-gradient(135deg, ${lightBlue[500]} 0%, ${teal[200]} 100%)`;

/**
 * Glass — the app's one translucent material.
 *
 * Figma node 1544:14457: `rgba(26, 52, 77, 0.5)` over a 8px backdrop blur, on
 * blueGrey/900. It is a material rather than an elevation step: it has no
 * shadow and reads as depth only because what is behind it stays visible.
 *
 * It needs something worth blurring behind it. Over a flat fill it is simply a
 * darker flat fill, and the blur costs a compositing layer for nothing.
 */
export const glass = {
  /** blueGrey/900 at half opacity. */
  fill: `rgba(26, 52, 77, 0.5)`,
  /** Goes on `backdropFilter`, not `filter` — `filter` would blur the content. */
  blur: 'blur(8px)',
} as const;

/** _native/scrollbar-fg and -bg. */
export const scrollbar = { fg: blueGrey[300], bg: blueGrey[100] } as const;

/**
 * OCID **designation** — who the holder is. Blue for a law enforcement officer,
 * slate for everyone else. Identical in both modes, because a credential looks
 * the same whatever scheme the app around it is in.
 *
 * Designation and status are two different questions and used to share one
 * object. A staff member can be verified or not; an officer is an officer
 * either way. Keeping them apart is what stops a card tinting itself by the
 * wrong axis.
 */
export const ocidDesignation = {
  leoLight: lightBlue[500],
  leoDark: '#0d47a1',
  nonLeoLight: blueGrey[100],
  nonLeoDark: blueGrey[800],
} as const;

/** OCID **status** — whether the credential checks out. Identical in both modes. */
export const ocidStatus = {
  verified: teal[200],
  notVerified: red[500],
} as const;

const onDark = '#000000de';

export const lightPalette = {
  primary: { main: blueGrey[800], dark: blueGrey[900], light: blueGrey[700], contrastText: '#ffffff' },
  secondary: { main: blueGrey[600], dark: blueGrey[700], light: blueGrey[500], contrastText: '#ffffff' },
  error: { main: red[700], dark: red[800], light: red[400], contrastText: '#ffffff' },
  warning: { main: amber[800], dark: amber[900], light: amber[700], contrastText: '#ffffff' },
  info: { main: lightBlue[700], dark: lightBlue[900], light: lightBlue[500], contrastText: '#ffffff' },
  success: { main: green[500], dark: green[600], light: green[400], contrastText: '#ffffff' },
  text: {
    primary: blueGrey[800],
    secondary: '#2b455ebd',
    disabled: '#2b455e61',
  },
  background: { default: blueGrey[50], paper: '#ffffff' },
  action: {
    active: '#2b455e8f',
    hover: '#2b455e0a',
    selected: surfaceTint,
    focus: '#2b455e1f',
    disabled: '#2b455e61',
    disabledBackground: '#2b455e1f',
  },
} as const;

export const darkPalette = {
  primary: { main: lightBlue[500], dark: lightBlue[600], light: lightBlue[400], contrastText: onDark },
  secondary: { main: blueGrey[300], dark: blueGrey[400], light: blueGrey[200], contrastText: onDark },
  error: { main: red[500], dark: red[700], light: red[300], contrastText: '#ffffff' },
  warning: { main: amber[500], dark: amber[700], light: amber[400], contrastText: onDark },
  info: { main: lightBlue[400], dark: lightBlue[700], light: lightBlue[300], contrastText: onDark },
  success: { main: green[400], dark: green[600], light: green[300], contrastText: onDark },
  text: {
    primary: '#ffffff',
    secondary: '#ffffffb2',
    disabled: '#ffffff61',
  },
  // Navy-tinted surfaces rather than MUI's neutral #121212.
  background: { default: '#091725', paper: '#0b1f33' },
  action: {
    active: '#ffffff8f',
    hover: '#ffffff14',
    selected: '#ffffff29',
    focus: '#ffffff1f',
    disabled: '#ffffff61',
    disabledBackground: '#ffffff1f',
  },
} as const;

/** Component-slot tokens that have no home in the MUI palette. */
export const componentTokens = {
  light: {
    avatarFill: blueGrey[300],
    avatarText: blueGrey[800],
    scrollbarFg: blueGrey[300],
    scrollbarBg: blueGrey[100],
    tooltipFill: '#2b455ee5',
    // _components/switch/slideFill. MUI's default resolves to common.black here.
    switchTrack: blueGrey[800],
  },
  dark: {
    avatarFill: blueGrey[600],
    avatarText: blueGrey[300],
    scrollbarFg: blueGrey[900],
    scrollbarBg: blueGrey[800],
    tooltipFill: '#616161e5',
    switchTrack: '#ffffff',
  },
} as const;

/** Rating active fill — the one amber that is not on the warning ramp. */
export const ratingActiveFill = '#ffb400';
